/**
 * DecisionFlow — ExposureMonitor
 * ui/src/components/finance/ExposureMonitor.jsx
 * S142 · Priority 5
 *
 * Exposure Monitor (Mode 2) — Finance shell main build.
 *
 * Sub-tasks completed (A–Q):
 * A: Header metadata strip
 * B: Summary chips
 * C: Top KPI band (5 cards)
 * D: Two-column layout shell (65% / 35%)
 * E: Right panel — population context table (suppression-aware)
 * F: Right panel — rule-based driver bullets
 * G: Left panel — bell curve canvas (normInv/normAtt)
 * H: Left panel — exposure summary strip
 * I: Left panel — payout exposure curve
 * J: Tab structure (5 tabs + source labels)
 * K: Actuals tab — wired to GET /api/v1/scenario-exposure + run selector
 * L: Typical SaaS / High Performance / Developing Team tabs (client-side math)
 * M: Custom tab (live mean+SD inputs)
 * N: Forecast Readiness card (GET /api/v1/finance-readiness)
 * O: Empty states per tab (informational only — no Finance CTAs)
 * P: Forecast Readiness card integrated
 * Q: Empty states
 *
 * S572-T1f: Run Exposure Analysis button + modal (Finance-triggered POST /scenario-exposure/compute)es per tab
 *
 * S480 Phase A fixes:
 * EM-01: aopVersion — fetch GET /api/v1/financial-plan-inputs, filter approved, read .version
 * EM-02: ote_currency_code — use row.ote_currency_code not hardcoded "USD"
 * EM-03: hcSnapshot — static "As of data load" label, not new Date()
 * EM-04: aboveQuotaPct — computed from erf in LeftPanel, passed as prop; labelled "Estimated"
 * EM-05: budget_headroom — use summary.budget_headroom_at_p90 directly from server
 * PlanVsLoadedPanel: new component, GET /api/v1/plan-vs-loaded, always visible below tabs
 *
 * S481 Phase B additions:
 * AopHcDeltaPanel: new component below PlanVsLoadedPanel — always visible, not tab-dependent
 * - Fetches GET /api/v1/aop-inputs?fiscal_period_id={id} when period changes
 * - Compares AOP planned_headcount vs loaded position headcount from population-breakdown
 * - Groups by role_code + segment_code + bu_code from aop-inputs response
 * - Join to population_breakdown on role_category — mismatch handled gracefully (show "—")
 * - Delta = loaded_hc - planned_hc: positive=over-hired (amber), negative=under-hired (red),
 * zero=on-plan (green)
 * - Finance language only: "Planned vs Loaded Headcount" header
 * - Empty state when no aop_input data: directs to Excel add-in
 * - L2.8 compliant: aggregate groups only, no per-position data
 *
 * Design constraints (non-negotiable):
 * - CC-1: No individual rep data — server-enforced
 * - Bell curve always ABOVE payout curve
 * - Population context panel FIXED across all tabs
 * - Actuals tab source = engine DB only — never distribution math
 * - Distribution tabs source = client-side normInv/normAtt against real loaded HC
 * - Accelerator = "No accelerator plan loaded" when projected_accelerator_component = 0
 * - Accelerator spend computed from payout_detail where accelerator_applied > 1.0 — fixed S645
 * - Small group suppression is server-side — never client-side filtering
 * - Source label required on every tab
 * - Finance language only — no statistical jargon
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
 TrendingUp, Users, DollarSign, Activity, CheckCircle,
 XCircle, AlertTriangle, ChevronDown, ChevronUp, Info,
} from "lucide-react";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Math helpers — reused from v1 ScenarioExposure
// ---------------------------------------------------------------------------

function normInv(p) {
 const a = [2.515517, 0.802853, 0.010328];
 const b = [1.432788, 0.189269, 0.001308];
 const t = Math.sqrt(-2 * Math.log(p < 0.5 ? p : 1 - p));
 const x =
 t -
 (a[0] + a[1] * t + a[2] * t * t) /
 (1 + b[0] * t + b[1] * t * t + b[2] * t * t * t);
 return p < 0.5 ? -x : x;
}

function normAtt(pct, mean, sd) {
 return Math.max(0, mean + normInv(pct) * sd);
}

function normalPDF(x, mean, sd) {
 if (sd <= 0) return 0;
 const z = (x - mean) / sd;
 return Math.exp(-0.5 * z * z) / (sd * Math.sqrt(2 * Math.PI));
}

// erf approximation — used for aboveQuotaPct in LeftPanel (EM-04)
function erf(x) {
 const t = 1 / (1 + 0.3275911 * Math.abs(x));
 const y =
 1 -
 (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t +
 0.254829592) *
 t *
 Math.exp(-x * x);
 return x >= 0 ? y : -y;
}

// ---------------------------------------------------------------------------
// Format helpers — Finance language only
// ---------------------------------------------------------------------------

function fmt(val, currency = "USD", digits = 0) {
 if (val == null || isNaN(Number(val))) return "—";
 return Number(val).toLocaleString("en-US", {
 style: "currency",
 currency,
 maximumFractionDigits: digits,
 });
}

function fmtPct(val, decimals = 1) {
 if (val == null || isNaN(Number(val))) return "—";
 return `${(Number(val) * 100).toFixed(decimals)}%`;
}

function fmtPctRaw(val, decimals = 1) {
 // val is already 0–100 range
 if (val == null || isNaN(Number(val))) return "—";
 return `${Number(val).toFixed(decimals)}%`;
}

function fmtPctSigned(val, decimals = 1) {
 // val is already 0–100 range, show sign
 if (val == null || isNaN(Number(val))) return "—";
 const n = Number(val);
 return `${n >= 0 ? "+" : ""}${n.toFixed(decimals)}%`;
}

function timeAgo(ts) {
 if (!ts) return "—";
 const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
 if (diff < 1) return "just now";
 if (diff === 1) return "1 min ago";
 if (diff < 60) return `${diff} min ago`;
 const hrs = Math.floor(diff / 60);
 return `${hrs}h ago`;
}

function shortId(id) {
 if (!id) return "—";
 return id.toString().slice(0, 8).toUpperCase();
}

// ---------------------------------------------------------------------------
// Loading / Error primitives
// ---------------------------------------------------------------------------

function LoadingRow() {
 return (
 <div className="animate-pulse space-y-2">
 <div className="h-4 w-2/3 rounded bg-[rgb(var(--color-surface-muted))]" />
 <div className="h-4 w-1/2 rounded bg-[rgb(var(--color-surface-muted))]" />
 </div>
 );
}

function EmptyState({ icon: Icon = Info, title, body }) {
 return (
 <div className="flex flex-col items-center justify-center rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-8 text-center">
 <Icon className="mb-3 h-8 w-8 text-[rgb(var(--color-text-muted))]" />
 <p className="text-sm font-medium text-[rgb(var(--color-text-muted))]">{title}</p>
 {body && <p className="mt-1 max-w-sm text-xs text-[rgb(var(--color-text-muted))]">{body}</p>}
 </div>
 );
}

// ---------------------------------------------------------------------------
// A: Header metadata strip
// EM-03: hcSnapshot — display as static string "As of data load", not new Date()
// ---------------------------------------------------------------------------

function HeaderMetadataStrip({ aopVersion, runId, planVersion, hcSnapshot }) {
 return (
 <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-2.5 text-xs">
 <span>
 <span className="text-[rgb(var(--color-text-muted))]">AOP Version: </span>
 <span className="font-medium text-[rgb(var(--color-text))]">
 {aopVersion ?? "—"}
 </span>
 </span>
 <span>
 <span className="text-[rgb(var(--color-text-muted))]">Calculation Run: </span>
 <span className="font-medium text-[rgb(var(--color-text))]">
 {runId ? shortId(runId) : "—"}
 </span>
 </span>
 <span>
 <span className="text-[rgb(var(--color-text-muted))]">Plan Version: </span>
 <span className="font-medium text-[rgb(var(--color-text))]">
 {planVersion ?? "—"}
 </span>
 </span>
 <span>
 <span className="text-[rgb(var(--color-text-muted))]">HC Snapshot: </span>
 <span className="font-medium text-[rgb(var(--color-text))]">
 {/* EM-03: render as-is — hcSnapshot is now a static label string, not a timestamp */}
 {hcSnapshot ?? "—"}
 </span>
 </span>
 </div>
 );
}

// ---------------------------------------------------------------------------
// B: Summary chips
// ---------------------------------------------------------------------------

function SummaryChips({ periodLabel, totalHC, currency, refreshedAt }) {
 return (
 <div className="flex flex-wrap gap-2">
 {[
 periodLabel ?? "—",
 totalHC != null ? `${totalHC} Loaded Roles` : "— Loaded Roles",
 currency ?? "USD",
 `Refreshed ${timeAgo(refreshedAt)}`,
 ].map((chip) => (
 <span
 key={chip}
 className="rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1 text-xs font-medium text-[rgb(var(--color-text-muted))]"
 >
 {chip}
 </span>
 ))}
 </div>
 );
}

// ---------------------------------------------------------------------------
// C: Top KPI band — 5 cards
// EM-05: Budget Variance card uses summary.budget_headroom_at_p90 directly
// from the scenario-exposure response — no client-side recompute.
// ---------------------------------------------------------------------------

function KPIBand({ budget, p50, p75, p90, headroomP50, headroomP75, headroomP90, currency }) {
 const cur = currency ?? "USD";

 // Compute variance label + colour for a given headroom value.
 // headroom = budget - projected: positive = under budget (green), negative = over (red).
 function varianceLine(headroom) {
 if (headroom == null) return { label: "—", over: false };
 const h = parseFloat(headroom);
 const over = h < 0;
 return {
 label: over
 ? `${fmt(Math.abs(h), cur)} over budget`
 : `${fmt(h, cur)} headroom`,
 over,
 };
 }

 const v50 = varianceLine(headroomP50);
 const v75 = varianceLine(headroomP75);
 const v90 = varianceLine(headroomP90);

 const cards = [
 {
 label: "Variable Comp Budget",
 value: fmt(budget, cur),
 variance: null,
 },
 {
 label: "Projected P50 Spend",
 value: fmt(p50, cur),
 sub: "50th percentile",
 variance: v50,
 },
 {
 label: "Projected P75 Spend",
 value: fmt(p75, cur),
 sub: "75th percentile",
 variance: v75,
 },
 {
 label: "Projected P90 Spend",
 value: fmt(p90, cur),
 sub: "90th percentile",
 variance: v90,
 },
 ];

 return (
 <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
 {cards.map((c) => (
 <div
 key={c.label}
 className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-3"
 >
 <div className="text-xs text-[rgb(var(--color-text-muted))]">{c.label}</div>
 <div className="mt-1 text-base font-bold leading-tight text-[rgb(var(--color-text))]">
 {c.value}
 </div>
 {c.sub && (
 <div className="mt-0.5 text-xs text-[rgb(var(--color-text-muted))]">{c.sub}</div>
 )}
 {c.variance && (
 <div className={`mt-1 text-xs font-semibold ${
 c.variance.over
 ? "text-[rgb(var(--color-danger))]"
 : "text-[rgb(var(--color-success))]"
 }`}>
 {c.variance.label}
 </div>
 )}
 </div>
 ))}
 </div>
 );
}

// ---------------------------------------------------------------------------
// E: Right panel — Population context table (suppression-aware)
// EM-02: use row.ote_currency_code || "USD" — not hardcoded "USD"
// ---------------------------------------------------------------------------

function PopulationContextTable({ population, loading, error }) {
 return (
 <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
 <div className="border-b border-[rgb(var(--color-border))] px-3 py-2">
 <div className="flex items-center gap-2">
 <Users className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
 <span className="text-xs font-semibold text-[rgb(var(--color-text))]">
 Loaded Population Context
 </span>
 </div>
 </div>
 <div className="overflow-x-auto">
 {loading && (
 <div className="p-3">
 <LoadingRow />
 </div>
 )}
 {error && (
 <div className="p-3 text-xs text-[rgb(var(--color-danger))]">
 Unable to load population data.
 </div>
 )}
 {!loading && !error && (!population || population.length === 0) && (
 <div className="p-3 text-xs text-[rgb(var(--color-text-muted))]">
 No workforce data loaded for this period. Contact your Comp Admin to load positions.
 </div>
 )}
 {!loading && !error && population && population.length > 0 && (
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-[rgb(var(--color-border))]">
 {["Role", "HC", "OTE Midpoint", "Var Mix", "Quota Cap"].map(
 (h) => (
 <th
 key={h}
 className="px-3 py-1.5 text-left font-medium text-[rgb(var(--color-text-muted))]"
 >
 {h}
 </th>
 )
 )}
 </tr>
 </thead>
 <tbody>
 {population.map((row) => (
 <tr
 key={row.role_category}
 className="border-b border-[rgb(var(--color-border))] last:border-0"
 >
 <td className="px-3 py-1.5 font-medium text-[rgb(var(--color-text))]">
 {row.role_category}
 </td>
 <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">
 {row.headcount}
 </td>
 {row.suppressed ? (
 <td
 colSpan={3}
 className="px-3 py-1.5 italic text-[rgb(var(--color-text-muted))]"
 >
 Included in Total — detail withheld
 </td>
 ) : (
 <>
 <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">
 {row.ote_midpoint
 ? fmt(parseFloat(row.ote_midpoint), row.ote_currency_code || "USD")
 : "—"}
 </td>
 <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">
 {row.variable_pct
 ? fmtPct(parseFloat(row.variable_pct))
 : "—"}
 </td>
 <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">
 {row.quota_capacity && parseFloat(row.quota_capacity) > 0
 ? fmt(parseFloat(row.quota_capacity), row.ote_currency_code || "USD")
 : "—"}
 </td>
 </>
 )}
 </tr>
 ))}
 </tbody>
 </table>
 )}
 </div>
 </div>
 );
}

// ---------------------------------------------------------------------------
// F: Right panel — Rule-based driver bullets
// EM-04: aboveQuotaPct received as prop computed from LeftPanel erf — not hardcoded.
// Label signals it is distribution-derived ("Elevated above-quota concentration (estimated)").
// ---------------------------------------------------------------------------

function DriverBullets({ exposure, population, aboveQuotaPct }) {
 const bullets = [];

 if (exposure) {
 const p90 = exposure.percentiles?.find((p) => p.scenario_code === "P90");
 const variancePct = p90 ? parseFloat(p90.variance_pct ?? 0) : 0;
 if (Math.abs(variancePct) > 15) {
 bullets.push("Elevated payout concentration vs budget");
 }

 const accelerator = p90
 ? parseFloat(p90.projected_accelerator_component ?? 0)
 : 0;
 const totalPayout = p90
 ? parseFloat(p90.projected_total_payout ?? 0)
 : 0;
 if (accelerator > 0 && totalPayout > 0 && accelerator / totalPayout > 0.2) {
 bullets.push("Material accelerator exposure");
 }
 }

 // EM-04: aboveQuotaPct is now distribution-derived from erf in LeftPanel.
 // Label as "Estimated" to signal it is not engine-provided per-rep attainment data.
 if (aboveQuotaPct != null && aboveQuotaPct > 0.25) {
 bullets.push("Elevated above-quota concentration (estimated)");
 }

 if (bullets.length === 0) {
 bullets.push("Payout exposure within expected range");
 }

 return (
 <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-3">
 <div className="mb-2 text-xs font-semibold text-[rgb(var(--color-text))]">
 Primary Exposure Drivers
 </div>
 <ul className="space-y-1.5">
 {bullets.map((b, i) => (
 <li key={i} className="flex items-start gap-2 text-xs text-[rgb(var(--color-text-muted))]">
 <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[rgb(var(--color-text-muted))]" />
 {b}
 </li>
 ))}
 </ul>
 </div>
 );
}

// ---------------------------------------------------------------------------
// G: Bell curve canvas
// ---------------------------------------------------------------------------

function BellCurveCanvas({ mean, sd, p50att, p75att, p90att }) {
 const canvasRef = useRef(null);

 useEffect(() => {
 const canvas = canvasRef.current;
 if (!canvas) return;

 // DPI fix — scale canvas backing store for high-DPI screens
 const dpr = window.devicePixelRatio || 1;
 const cssW = 540;
 const cssH = 160;
 canvas.width = cssW * dpr;
 canvas.height = cssH * dpr;
 canvas.style.width = `${cssW}px`;
 canvas.style.height = `${cssH}px`;

 const ctx = canvas.getContext("2d");
 ctx.scale(dpr, dpr);
 const W = cssW;
 const H = cssH;
 ctx.clearRect(0, 0, W, H);

 // Resolve CSS design tokens for canvas — getComputedStyle required (canvas != DOM)
 // resolve() produces valid rgb() strings for strokes/text. Fill areas use original
 // safe rgba literals — CSS var values are space-separated triplets (e.g. "99 102 241")
 // so wrapping them in rgba() inline produces invalid values. Literals are intentional.
 const style = getComputedStyle(document.documentElement);
 const resolve = (v) => `rgb(${style.getPropertyValue(v).trim()})`;
 const textColor   = resolve("--color-text-muted");
 const gridColor   = resolve("--color-surface-muted");
 const curveColor  = resolve("--color-primary");
 const markerColor = resolve("--color-border");
 const accelColor  = "rgba(251,146,60,0.12)";
 const curveFill   = "rgba(99,102,241,0.12)";

 // X axis: 0% to 175% attainment
 const xMin = 0;
 const xMax = 1.75;
 const padL = 36, padR = 16, padT = 12, padB = 28;
 const plotW = W - padL - padR;
 const plotH = H - padT - padB;

 function xPx(att) {
 return padL + ((att - xMin) / (xMax - xMin)) * plotW;
 }
 function yPx(val, maxVal) {
 return padT + plotH - (val / maxVal) * plotH;
 }

 // Compute curve points
 const steps = 200;
 const xs = Array.from({ length: steps }, (_, i) => xMin + (i / (steps - 1)) * (xMax - xMin));
 const ys = xs.map((x) => normalPDF(x, mean, sd));
 const maxY = Math.max(...ys) * 1.1;

 // Grid
 ctx.strokeStyle = gridColor;
 ctx.lineWidth = 1;
 [0.5, 0.75, 1.0, 1.25, 1.5].forEach((att) => {
 const x = xPx(att);
 ctx.beginPath();
 ctx.moveTo(x, padT);
 ctx.lineTo(x, padT + plotH);
 ctx.stroke();
 });

 // Accelerator zone fill (right of 100%)
 const x100 = xPx(1.0);
 const xEnd = xPx(xMax);
 ctx.fillStyle = accelColor;
 ctx.fillRect(x100, padT, xEnd - x100, plotH);

 // Accelerator zone label
 ctx.fillStyle = resolve("--color-warning");
 ctx.font = "9px sans-serif";
 ctx.fillText("Accelerator Zone", x100 + 4, padT + 10);

 // Bell curve fill
 ctx.beginPath();
 ctx.moveTo(xPx(xs[0]), yPx(ys[0], maxY));
 xs.forEach((x, i) => ctx.lineTo(xPx(x), yPx(ys[i], maxY)));
 ctx.lineTo(xPx(xs[xs.length - 1]), padT + plotH);
 ctx.lineTo(xPx(xs[0]), padT + plotH);
 ctx.closePath();
 ctx.fillStyle = curveFill;
 ctx.fill();

 // Bell curve line
 ctx.beginPath();
 ctx.strokeStyle = curveColor;
 ctx.lineWidth = 2;
 xs.forEach((x, i) => {
 const px = xPx(x);
 const py = yPx(ys[i], maxY);
 i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
 });
 ctx.stroke();

 // P-markers
 const markers = [
 { att: p50att, label: "P50", color: resolve("--color-success") },
 { att: p75att, label: "P75", color: resolve("--color-primary") },
 { att: p90att, label: "P90", color: resolve("--color-danger") },
 ];
 markers.forEach(({ att, label, color }) => {
 if (att == null || isNaN(att)) return;
 const x = xPx(att);
 ctx.strokeStyle = color;
 ctx.lineWidth = 1.5;
 ctx.setLineDash([3, 3]);
 ctx.beginPath();
 ctx.moveTo(x, padT);
 ctx.lineTo(x, padT + plotH);
 ctx.stroke();
 ctx.setLineDash([]);
 ctx.fillStyle = color;
 ctx.font = "bold 9px sans-serif";
 ctx.fillText(label, x + 2, padT + 18);
 });

 // 100% quota line
 ctx.strokeStyle = markerColor;
 ctx.lineWidth = 1.5;
 ctx.setLineDash([]);
 ctx.beginPath();
 ctx.moveTo(x100, padT);
 ctx.lineTo(x100, padT + plotH);
 ctx.stroke();

 // X-axis labels
 ctx.fillStyle = textColor;
 ctx.font = "9px sans-serif";
 ctx.textAlign = "center";
 [0.5, 0.75, 1.0, 1.25, 1.5].forEach((att) => {
 ctx.fillText(`${(att * 100).toFixed(0)}%`, xPx(att), padT + plotH + 14);
 });

 // Title
 ctx.textAlign = "left";
 ctx.fillStyle = textColor;
 ctx.font = "10px sans-serif";
 ctx.fillText("Expected Attainment Distribution", padL, padT - 2);
 }, [mean, sd, p50att, p75att, p90att]);

 return (
 <canvas
 ref={canvasRef}
 width={540}
 height={160}
 className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]"
 style={{ maxHeight: 160 }}
 />
 );
}

// ---------------------------------------------------------------------------
// H: Exposure summary strip
// ---------------------------------------------------------------------------

function ExposureSummaryStrip({ mean, sd, aboveQuotaPct, acceleratorValue, currency }) {
 const items = [
 {
 label: "Average Attainment",
 value: fmtPctRaw(mean * 100),
 },
 {
 label: "Performance Spread",
 value: fmtPctRaw(sd * 100),
 },
 {
 label: "Population Above 100%",
 value: fmtPctRaw(aboveQuotaPct * 100),
 },
 {
 label: "Incremental Accelerator Spend",
 value:
 acceleratorValue != null && parseFloat(acceleratorValue) > 0
 ? fmt(parseFloat(acceleratorValue), currency ?? "USD")
 : "No accelerator plan loaded",
 },
 ];

 return (
 <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
 {items.map((item) => (
 <div
 key={item.label}
 className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-2.5"
 >
 <div className="text-xs text-[rgb(var(--color-text-muted))]">{item.label}</div>
 <div className="mt-0.5 text-sm font-semibold text-[rgb(var(--color-text))]">
 {item.value}
 </div>
 </div>
 ))}
 </div>
 );
}

// ---------------------------------------------------------------------------
// I: Payout exposure curve
// ---------------------------------------------------------------------------

function PayoutExposureCurve({ budget, p50, p75, p90, currency }) {
 const canvasRef = useRef(null);
 const cur = currency ?? "USD";

 useEffect(() => {
 const canvas = canvasRef.current;
 if (!canvas) return;

 // DPI fix — scale canvas backing store for high-DPI screens
 const dpr = window.devicePixelRatio || 1;
 const cssW = 540;
 const cssH = 120;
 canvas.width = cssW * dpr;
 canvas.height = cssH * dpr;
 canvas.style.width = `${cssW}px`;
 canvas.style.height = `${cssH}px`;

 const ctx = canvas.getContext("2d");
 ctx.scale(dpr, dpr);
 const W = cssW;
 const H = cssH;
 ctx.clearRect(0, 0, W, H);

 // Resolve CSS design tokens for canvas
 const style = getComputedStyle(document.documentElement);
 const resolve = (v) => `rgb(${style.getPropertyValue(v).trim()})`;
 const textColor = resolve("--color-text-muted");
 const gridColor = resolve("--color-surface-muted");

 // Budget is the reference line — P50/P75/P90 are the scenario bars
 const budgetVal = parseFloat(budget ?? 0);
 const scenarioBars = [
 {
   label: "P50",
   value: parseFloat(p50 ?? 0),
   fill: "rgba(16,185,129,0.18)",
   stroke: resolve("--color-success"),
 },
 {
   label: "P75",
   value: parseFloat(p75 ?? 0),
   fill: "rgba(245,158,11,0.18)",
   stroke: resolve("--color-warning"),
 },
 {
   label: "P90",
   value: parseFloat(p90 ?? 0),
   fill: "rgba(239,68,68,0.18)",
   stroke: resolve("--color-danger"),
 },
 ];

 const allValues = [budgetVal, ...scenarioBars.map((b) => b.value)];
 const maxVal = Math.max(...allValues) * 1.18 || 1;
 const padL = 60, padR = 20, padT = 16, padB = 24;
 const plotW = W - padL - padR;
 const plotH = H - padT - padB;

 function xPx(v) {
 return padL + (v / maxVal) * plotW;
 }

 // Grid lines
 [0.25, 0.5, 0.75, 1.0].forEach((pct) => {
 ctx.strokeStyle = gridColor;
 ctx.lineWidth = 1;
 ctx.setLineDash([]);
 ctx.beginPath();
 ctx.moveTo(padL + pct * plotW, padT);
 ctx.lineTo(padL + pct * plotW, padT + plotH);
 ctx.stroke();
 });

 const laneH = plotH / scenarioBars.length;

 // Draw scenario bars (P50/P75/P90) — filled with gradient color
 scenarioBars.forEach((bar, i) => {
 const y = padT + i * laneH + laneH / 2;
 const barH = Math.min(laneH * 0.48, 18);
 const barW = xPx(bar.value) - padL;
 const fill = bar.fill;
 const stroke = bar.stroke;

 // Filled bar
 ctx.fillStyle = fill;
 ctx.setLineDash([]);
 ctx.beginPath();
 ctx.roundRect(padL, y - barH / 2, Math.max(barW, 2), barH, 3);
 ctx.fill();

 // Solid border
 ctx.strokeStyle = stroke;
 ctx.lineWidth = 1.5;
 ctx.beginPath();
 ctx.roundRect(padL, y - barH / 2, Math.max(barW, 2), barH, 3);
 ctx.stroke();

 // Row label (left)
 ctx.fillStyle = textColor;
 ctx.font = "bold 9px sans-serif";
 ctx.textAlign = "right";
 ctx.fillText(bar.label, padL - 4, y + 3);

 // Value (right of bar)
 ctx.fillStyle = stroke;
 ctx.textAlign = "left";
 ctx.font = "9px sans-serif";
 ctx.fillText(
 bar.value > 0
 ? bar.value.toLocaleString("en-US", { style: "currency", currency: cur, maximumFractionDigits: 0 })
 : "—",
 xPx(bar.value) + 5,
 y + 3,
 );
 });

 // Budget reference line — dashed vertical + label above
 if (budgetVal > 0) {
 const budgetX = xPx(budgetVal);
 const refColor = resolve("--color-text-muted");

 ctx.strokeStyle = refColor;
 ctx.lineWidth = 1.5;
 ctx.setLineDash([5, 3]);
 ctx.beginPath();
 ctx.moveTo(budgetX, padT);
 ctx.lineTo(budgetX, padT + plotH);
 ctx.stroke();
 ctx.setLineDash([]);

 ctx.fillStyle = refColor;
 ctx.font = "bold 8px sans-serif";
 ctx.textAlign = "center";
 ctx.fillText("Budget", budgetX, padT - 4);
 }

 // Title
 ctx.fillStyle = textColor;
 ctx.font = "10px sans-serif";
 ctx.textAlign = "left";
 ctx.fillText("Payout Exposure", padL, padT - 4);
 }, [budget, p50, p75, p90, currency]);

 return (
 <canvas
 ref={canvasRef}
 width={540}
 height={120}
 className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]"
 style={{ maxHeight: 120 }}
 />
 );
}

// ---------------------------------------------------------------------------
// N / P: Forecast Readiness card
// ---------------------------------------------------------------------------

function ForecastReadinessCard({ readiness, loading, error }) {
 if (loading) return <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4"><LoadingRow /></div>;
 if (error) return null;
 if (!readiness) return null;

 const score = readiness.readiness_score ?? 0;
 const limited = readiness.is_limited_forecast;

 const checks = readiness.checks ?? {};
 const rows = [
 { label: "Active HC Loaded", pass: checks.active_hc_loaded?.pass, detail: checks.active_hc_loaded?.count != null ? `${checks.active_hc_loaded.count} positions` : null },
 { label: "Approved Comp Structures", pass: checks.approved_comp_structures?.pass, detail: checks.approved_comp_structures?.count != null ? `${checks.approved_comp_structures.count} structures` : null },
 { label: "Quota Coverage", pass: checks.quota_coverage_pct?.pass, detail: checks.quota_coverage_pct?.value != null ? fmtPct(parseFloat(checks.quota_coverage_pct.value)) : null },
 { label: "Rate Table Coverage", pass: checks.rate_table_coverage_pct?.pass, detail: checks.rate_table_coverage_pct?.value != null ? fmtPct(parseFloat(checks.rate_table_coverage_pct.value)) : null },
 { label: "AOP Approved", pass: checks.aop_approved?.pass, detail: null },
 ];

 return (
 <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
 <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
 <div className="flex items-center gap-2">
 <Activity className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
 <span className="text-sm font-semibold text-[rgb(var(--color-text))]">
 Forecast Readiness
 </span>
 </div>
 <div className="flex items-center gap-2">
 <span
 className={`text-lg font-bold ${
 score >= 80
 ? "text-[rgb(var(--color-success))]"
 : "text-[rgb(var(--color-warning))]"
 }`}
 >
 {score}%
 </span>
 {limited && (
 <span className="rounded-full bg-[rgb(var(--color-warning))]/15 px-2 py-0.5 text-xs font-medium text-[rgb(var(--color-warning))]">
 Limited Forecast
 </span>
 )}
 </div>
 </div>
 <div className="divide-y divide-[rgb(var(--color-border))]">
 {rows.map(({ label, pass, detail }) => (
 <div key={label} className="flex items-center justify-between px-4 py-2">
 <div className="flex items-center gap-2">
 {pass ? (
 <CheckCircle className="h-3.5 w-3.5 text-[rgb(var(--color-success))]" />
 ) : (
 <XCircle className="h-3.5 w-3.5 text-[rgb(var(--color-danger))]" />
 )}
 <span className="text-xs text-[rgb(var(--color-text-muted))]">{label}</span>
 </div>
 <span className="text-xs font-medium text-[rgb(var(--color-text-muted))]">
 {detail ?? (pass ? "Complete" : "Incomplete")}
 </span>
 </div>
 ))}
 </div>
 {limited && (
 <div className="border-t border-[rgb(var(--color-border))] px-4 py-2">
 <p className="text-xs text-[rgb(var(--color-warning))]">
 Limited Forecast — incomplete quota or rate coverage. Numbers shown are directional only.
 </p>
 </div>
 )}
 </div>
 );
}

// ---------------------------------------------------------------------------
// Tab definitions — J
// ---------------------------------------------------------------------------

// S608: Enriched tab definitions — each distribution scenario has a visual
// identity card (color accent, description, key stats) rendered as pattern
// selector cards above the bell curve, not just text tabs.
const TABS = [
 {
 id: "actuals",
 label: "Actuals",
 sourceLabel: "Based on completed calculation run",
 desc: "Engine-computed payout data from the selected final calculation run.",
 accent: "primary",
 stat: "Live data",
 mean: null,
 sd: null,
 },
 {
 id: "typical",
 label: "Typical SaaS",
 sourceLabel: "Based on standard SaaS planning distribution",
 desc: "Most reps slightly below quota. A few overachievers drive the right tail.",
 accent: "success",
 stat: "Mean 85% · Spread 25%",
 mean: 0.85,
 sd: 0.25,
 },
 {
 id: "high",
 label: "High Performance",
 sourceLabel: "Based on high-attainment planning distribution",
 desc: "Experienced team clustered tightly around quota. Fewer outliers below.",
 accent: "warning",
 stat: "Mean 100% · Spread 15%",
 mean: 1.0,
 sd: 0.15,
 },
 {
 id: "developing",
 label: "Developing Team",
 sourceLabel: "Based on broad-variance planning distribution",
 desc: "New team or market. Most reps below quota. Wide performance dispersion.",
 accent: "danger",
 stat: "Mean 70% · Spread 30%",
 mean: 0.7,
 sd: 0.3,
 },
 {
 id: "custom",
 label: "Custom",
 sourceLabel: "Based on Finance-defined assumptions",
 desc: "Enter your own historical attainment mean and standard deviation.",
 accent: "text-muted",
 stat: "User-defined",
 mean: null,
 sd: null,
 },
];

// ---------------------------------------------------------------------------
// Left panel — visualization stack for a given tab
// EM-04: aboveQuotaPct computed here from erf, passed as prop to DriverBullets
// ---------------------------------------------------------------------------

function LeftPanel({ tab, exposure, budget, currency, customMean, customSd, onAboveQuotaPct }) {
 const cur = currency ?? "USD";

 // Resolve mean/sd for this tab
 let mean, sd;
 if (tab.id === "actuals") {
 // Use Typical SaaS as visual approximation until engine provides per-rep attainment
 mean = 0.85; sd = 0.25;
 } else if (tab.id === "custom") {
 mean = parseFloat(customMean) / 100 || 0.85;
 sd = parseFloat(customSd) / 100 || 0.25;
 } else {
 mean = tab.mean;
 sd = tab.sd;
 }

 const p50att = normAtt(0.50, mean, sd);
 const p75att = normAtt(0.75, mean, sd);
 const p90att = normAtt(0.90, mean, sd);

 const budgetNum = parseFloat(budget ?? 0);

 // For Actuals tab use engine data; for distribution tabs use client-side math
 let p50, p75, p90, acceleratorValue;
 if (tab.id === "actuals" && exposure) {
 const summary = exposure.summary ?? {};
 p50 = parseFloat(summary.p50_projected_payout ?? 0);
 p75 = parseFloat(summary.p75_projected_payout ?? 0);
 p90 = parseFloat(summary.p90_projected_payout ?? 0);
 const p90row = exposure.percentiles?.find((p) => p.scenario_code === "P90");
 acceleratorValue = p90row ? parseFloat(p90row.projected_accelerator_component ?? 0) : 0;
 } else {
 p50 = budgetNum * p50att;
 p75 = budgetNum * p75att;
 p90 = budgetNum * p90att;
 acceleratorValue = 0;
 }

 // EM-04: compute aboveQuotaPct from distribution using erf — notify parent via callback
 const aboveQuotaPct = 1 - (0.5 * (1 + erf((1.0 - mean) / (sd * Math.SQRT2))));

 useEffect(() => {
 if (onAboveQuotaPct) onAboveQuotaPct(aboveQuotaPct);
 }, [aboveQuotaPct, onAboveQuotaPct]);

 return (
 <div className="space-y-3">
 {/* G: Bell curve — ABOVE payout curve always */}
 <BellCurveCanvas
 mean={mean}
 sd={sd}
 p50att={p50att}
 p75att={p75att}
 p90att={p90att}
 />

 {/* H: Exposure summary strip */}
 <ExposureSummaryStrip
 mean={mean}
 sd={sd}
 aboveQuotaPct={aboveQuotaPct}
 acceleratorValue={acceleratorValue}
 currency={cur}
 />

 {/* I: Payout exposure curve */}
 <PayoutExposureCurve
 budget={budgetNum}
 p50={p50}
 p75={p75}
 p90={p90}
 currency={cur}
 />
 </div>
 );
}

// ---------------------------------------------------------------------------
// Run selector — two-level (fiscal period → run within period) — K
// ---------------------------------------------------------------------------

function RunSelector({ runs, selectedRunId, onSelect }) {
 if (!runs || runs.length === 0) {
 return (
 <div className="text-xs text-[rgb(var(--color-text-muted))]">No completed calculation runs found for this period.</div>
 );
 }
 return (
 <div className="flex items-center gap-3">
 <div className="flex-1">
 <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
 Calculation Run
 </label>
 <select
 value={selectedRunId}
 onChange={(e) => onSelect(e.target.value)}
 className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-xs"
 >
 {runs.map((r) => {
 const id = r.calculation_run_id || r.id;
 return (
 <option key={id} value={id}>
 {r.run_type === "final" ? "Final" : "Preview"} —{" "}
 {r.created_at
 ? new Date(r.created_at).toLocaleDateString()
 : shortId(id)}
 </option>
 );
 })}
 </select>
 </div>
 </div>
 );
}

// ---------------------------------------------------------------------------
// AOP Scope badge — PlanVsLoadedPanel helper
// ---------------------------------------------------------------------------

const AOP_SCOPE_LABEL = {
 bu_segment: "BU + Segment",
 bu: "BU",
 segment: "Segment",
 global: "Global",
 no_aop: "No AOP",
};

const AOP_SCOPE_COLOR = {
 bu_segment: "bg-[rgb(var(--color-success))]/15 text-[rgb(var(--color-success))]",
 bu: "bg-[rgb(var(--color-primary))]/15 text-[rgb(var(--color-primary))]",
 segment: "bg-[rgb(var(--color-primary))]/15 text-[rgb(var(--color-primary))]",
 global: "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]",
 no_aop: "bg-[rgb(var(--color-danger))]/15 text-[rgb(var(--color-danger))]",
};

function AopScopeBadge({ scope }) {
 const label = AOP_SCOPE_LABEL[scope] ?? scope;
 const color = AOP_SCOPE_COLOR[scope] ?? AOP_SCOPE_COLOR.global;
 return (
 <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${color}`}>
 {label}
 </span>
 );
}

// ---------------------------------------------------------------------------
// PlanVsLoadedPanel — S480 Phase A (OI-PLAN-VS-LOADED-SURFACE)
//
// Calls GET /api/v1/plan-vs-loaded?fiscal_period_id={id}
// Always visible below tab structure — not tab-dependent.
// Handles AOP_NOT_LOADED 422 → empty state.
// L2.8 clean by construction — no position identifiers in response.
// Columns: Role, Segment, BU, Plan Budget, Loaded Quota, Variance, Variance %, AOP Scope
// ---------------------------------------------------------------------------

function PlanVsLoadedPanel({ fiscalPeriodId }) {
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState(null);
 const [aopNotLoaded, setAopNotLoaded] = useState(false);

 useEffect(() => {
 if (!fiscalPeriodId) return;
 setLoading(true);
 setError(null);
 setAopNotLoaded(false);
 setData(null);

 fetch(
 `${API_BASE}/plan-vs-loaded?fiscal_period_id=${encodeURIComponent(fiscalPeriodId)}`,
 { credentials: "include" }
 )
 .then(async (r) => {
 if (r.status === 422) {
 const body = await r.json().catch(() => ({}));
 if (body?.error?.code === "AOP_NOT_LOADED") {
 setAopNotLoaded(true);
 return null;
 }
 throw new Error(`${r.status}`);
 }
 if (!r.ok) throw new Error(`${r.status}`);
 return r.json();
 })
 .then((d) => { if (d) setData(d); })
 .catch((e) => setError(e.message))
 .finally(() => setLoading(false));
 }, [fiscalPeriodId]);

 const rows = data?.rows ?? [];
 const totals = data?.totals ?? null;
 const currency = data?.aop_currency_code ?? "USD";

 return (
 <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
 <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
 <div className="flex items-center gap-2">
 <TrendingUp className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
 <span className="text-sm font-semibold text-[rgb(var(--color-text))]">
 Plan vs Loaded Quota
 </span>
 {data?.fiscal_period_label && (
 <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-xs text-[rgb(var(--color-text-muted))]">
 {data.fiscal_period_label}
 </span>
 )}
 </div>
 {data?.as_of && (
 <span className="text-xs text-[rgb(var(--color-text-muted))]">
 As of {new Date(data.as_of).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
 </span>
 )}
 </div>

 {/* Loading */}
 {loading && (
 <div className="p-4">
 <LoadingRow />
 </div>
 )}

 {/* No fiscal period selected */}
 {!loading && !fiscalPeriodId && (
 <div className="p-4">
 <EmptyState
 icon={Info}
 title="No Active Period"
 body="An open fiscal period is required to load the plan vs quota comparison."
 />
 </div>
 )}

 {/* AOP_NOT_LOADED 422 */}
 {!loading && aopNotLoaded && (
 <div className="p-4">
 <EmptyState
 icon={AlertTriangle}
 title="No Approved AOP Budget for This Period"
 body="Create one in Plan Inputs."
 />
 </div>
 )}

 {/* Error */}
 {!loading && error && !aopNotLoaded && (
 <div className="p-4 text-xs text-[rgb(var(--color-danger))]">
 Unable to load plan vs loaded data.
 </div>
 )}

 {/* Empty rows (AOP loaded but no quota groups) */}
 {!loading && !error && !aopNotLoaded && data && rows.length === 0 && (
 <div className="p-4">
 <EmptyState
 icon={Users}
 title="No Quota Data Loaded"
 body="No active positions with quota found for this period. Contact your Comp Admin."
 />
 </div>
 )}

 {/* Data table */}
 {!loading && !error && !aopNotLoaded && rows.length > 0 && (
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-[rgb(var(--color-border))]">
 {["Role", "Segment", "BU", "Plan Budget", "Loaded Quota", "Variance", "Variance %", "AOP Scope"].map((h) => (
 <th
 key={h}
 className="px-3 py-2 text-left font-medium text-[rgb(var(--color-text-muted))] whitespace-nowrap"
 >
 {h}
 </th>
 ))}
 </tr>
 </thead>
 <tbody>
 {rows.map((row, i) => {
 const varianceNum = parseFloat(row.variance ?? 0);
 const variancePctNum = parseFloat(row.variance_pct ?? 0);
 const isOver = varianceNum < 0; // plan < loaded = over-deployed
 return (
 <tr
 key={i}
 className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))]"
 >
 <td className="px-3 py-2 font-medium text-[rgb(var(--color-text))] whitespace-nowrap">
 {row.role_name ?? "—"}
 </td>
 <td className="px-3 py-2 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
 {row.segment_name ?? "—"}
 </td>
 <td className="px-3 py-2 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
 {row.business_unit_name ?? "—"}
 </td>
 <td className="px-3 py-2 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
 {fmt(parseFloat(row.plan_variable_comp_budget ?? 0), currency)}
 </td>
 <td className="px-3 py-2 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
 {fmt(parseFloat(row.loaded_effective_quota ?? 0), currency)}
 </td>
 <td className={`px-3 py-2 font-medium whitespace-nowrap ${isOver ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-success))]"}`}>
 {isOver ? "-" : "+"}{fmt(Math.abs(varianceNum), currency)}
 </td>
 <td className={`px-3 py-2 font-medium whitespace-nowrap ${isOver ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-success))]"}`}>
 {fmtPctSigned(-variancePctNum / 100)}
 </td>
 <td className="px-3 py-2 whitespace-nowrap">
 <AopScopeBadge scope={row.aop_scope} />
 </td>
 </tr>
 );
 })}
 </tbody>
 {/* Totals footer */}
 {totals && (
 <tfoot>
 <tr className="border-t-2 border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] /50">
 <td colSpan={3} className="px-3 py-2 text-xs font-semibold text-[rgb(var(--color-text-muted))]">
 Total ({totals.position_count ?? 0} positions)
 </td>
 <td className="px-3 py-2 text-xs font-semibold text-[rgb(var(--color-text))] whitespace-nowrap">
 {fmt(parseFloat(totals.plan_variable_comp_budget ?? 0), currency)}
 </td>
 <td className="px-3 py-2 text-xs font-semibold text-[rgb(var(--color-text))] whitespace-nowrap">
 {fmt(parseFloat(totals.loaded_effective_quota ?? 0), currency)}
 </td>
 {(() => {
 const tv = parseFloat(totals.variance ?? 0);
 const tp = parseFloat(totals.variance_pct ?? 0);
 const over = tv < 0;
 return (
 <>
 <td className={`px-3 py-2 text-xs font-semibold whitespace-nowrap ${over ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-success))]"}`}>
 {over ? "-" : "+"}{fmt(Math.abs(tv), currency)}
 </td>
 <td className={`px-3 py-2 text-xs font-semibold whitespace-nowrap ${over ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-success))]"}`}>
 {fmtPctSigned(-tp / 100)}
 </td>
 </>
 );
 })()}
 <td className="px-3 py-2" />
 </tr>
 </tfoot>
 )}
 </table>
 </div>
 )}
 </div>
 );
}

// ---------------------------------------------------------------------------
// AopHcDeltaPanel — S481 Phase B (OI-S414-AOP-SPEC Phase B)
//
// Fetches GET /api/v1/aop-inputs?fiscal_period_id={id}
// Fetches population-breakdown (passed as prop — already loaded by parent)
// Always visible below PlanVsLoadedPanel — not tab-dependent.
// L2.8 compliant — aggregate groups only, no position identifiers.
//
// aop-inputs response shape:
// { data: [{ role_code, segment_code, bu_code,
// total_planned_variable_comp, total_planned_headcount,
// row_count, currency_code, fiscal_period_id }] }
//
// population-breakdown uses role_category (not role_code).
// Join mismatch handled gracefully: rows with no population match show
// loaded HC as "—" rather than failing.
//
// Delta = loaded_hc - planned_hc
// positive → over-hired (amber)
// negative → under-hired (red)
// zero → on-plan (green)
// ---------------------------------------------------------------------------

function AopHcDeltaPanel({ fiscalPeriodId, population }) {
 const [aopData, setAopData] = useState(null);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState(null);

 useEffect(() => {
 if (!fiscalPeriodId) return;
 setLoading(true);
 setError(null);
 setAopData(null);

 fetch(
 `${API_BASE}/aop-inputs?fiscal_period_id=${encodeURIComponent(fiscalPeriodId)}`,
 { credentials: "include" }
 )
 .then(async (r) => {
 if (!r.ok) throw new Error(`${r.status}`);
 return r.json();
 })
 .then((d) => setAopData(d))
 .catch((e) => setError(e.message))
 .finally(() => setLoading(false));
 }, [fiscalPeriodId]);

 const aopRows = aopData?.data ?? [];

 // Build a lookup from population-breakdown: role_category → headcount
 // population_breakdown uses role_category; aop-inputs uses role_code.
 // Match is best-effort — role granularity may differ.
 const popByRole = {};
 if (population?.population_breakdown) {
 for (const row of population.population_breakdown) {
 if (row.role_category && !row.suppressed) {
 // Accumulate headcount per role_category in case of multiple segments
 popByRole[row.role_category] = (popByRole[row.role_category] ?? 0) + (row.headcount ?? 0);
 }
 }
 }

 // Delta colour helpers
 function deltaClass(delta) {
 if (delta == null) return "text-[rgb(var(--color-text-muted))]";
 if (delta > 0) return "text-[rgb(var(--color-warning))]";
 if (delta < 0) return "text-[rgb(var(--color-danger))]";
 return "text-[rgb(var(--color-success))]";
 }

 function deltaLabel(delta) {
 if (delta == null) return "—";
 if (delta > 0) return `+${delta} Over`;
 if (delta < 0) return `${delta} Under`;
 return "On Plan";
 }

 return (
 <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
 {/* Header */}
 <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
 <div className="flex items-center gap-2">
 <Users className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
 <span className="text-sm font-semibold text-[rgb(var(--color-text))]">
 Planned vs Loaded Headcount
 </span>
 </div>
 <span className="text-xs text-[rgb(var(--color-text-muted))]">AOP HC Delta · Aggregate only</span>
 </div>

 {/* Loading */}
 {loading && (
 <div className="p-4">
 <LoadingRow />
 </div>
 )}

 {/* No fiscal period */}
 {!loading && !fiscalPeriodId && (
 <div className="p-4">
 <EmptyState
 icon={Info}
 title="No Active Period"
 body="An open fiscal period is required to load AOP headcount data."
 />
 </div>
 )}

 {/* Error */}
 {!loading && error && (
 <div className="p-4 text-xs text-[rgb(var(--color-danger))]">
 Unable to load AOP headcount data.
 </div>
 )}

 {/* Empty state — no AOP inputs loaded */}
 {!loading && !error && fiscalPeriodId && aopRows.length === 0 && (
 <div className="p-4">
 <EmptyState
 icon={AlertTriangle}
 title="No AOP Inputs Loaded for This Period"
 body="Upload AOP data in the Excel add-in."
 />
 </div>
 )}

 {/* Data table */}
 {!loading && !error && aopRows.length > 0 && (
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-[rgb(var(--color-border))]">
 {[
 "Role Code",
 "Segment",
 "BU",
 "AOP Planned HC",
 "Loaded HC",
 "Delta",
 "Status",
 ].map((h) => (
 <th
 key={h}
 className="px-3 py-2 text-left font-medium text-[rgb(var(--color-text-muted))] whitespace-nowrap"
 >
 {h}
 </th>
 ))}
 </tr>
 </thead>
 <tbody>
 {aopRows.map((row, i) => {
 const plannedHc = row.total_planned_headcount ?? null;
 // Best-effort join on role_code → role_category
 // role_granularity may differ — show "—" if no match found
 const loadedHc =
 row.role_code != null && popByRole[row.role_code] != null
 ? popByRole[row.role_code]
 : null;
 const delta =
 loadedHc != null && plannedHc != null
 ? loadedHc - plannedHc
 : null;

 return (
 <tr
 key={i}
 className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))]"
 >
 <td className="px-3 py-2 font-medium text-[rgb(var(--color-text))] whitespace-nowrap">
 {row.role_code ?? "—"}
 </td>
 <td className="px-3 py-2 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
 {row.segment_code ?? "—"}
 </td>
 <td className="px-3 py-2 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
 {row.bu_code ?? "—"}
 </td>
 <td className="px-3 py-2 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
 {plannedHc ?? "—"}
 </td>
 <td className="px-3 py-2 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
 {loadedHc != null ? loadedHc : "—"}
 </td>
 <td className={`px-3 py-2 font-medium whitespace-nowrap ${deltaClass(delta)}`}>
 {delta != null ? (delta >= 0 ? `+${delta}` : `${delta}`) : "—"}
 </td>
 <td className={`px-3 py-2 font-semibold whitespace-nowrap ${deltaClass(delta)}`}>
 {deltaLabel(delta)}
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>

 {/* Legend */}
 <div className="flex items-center gap-4 border-t border-[rgb(var(--color-border))] px-4 py-2">
 <span className="text-[10px] text-[rgb(var(--color-text-muted))]">
 Delta = Loaded HC − AOP Planned HC
 </span>
 <span className="text-[10px] text-[rgb(var(--color-warning))]">■ Over-hired</span>
 <span className="text-[10px] text-[rgb(var(--color-danger))]">■ Under-hired</span>
 <span className="text-[10px] text-[rgb(var(--color-success))]">■ On Plan</span>
 <span className="text-[10px] text-[rgb(var(--color-text-muted))] ml-auto italic">
 Loaded HC match requires role_code = role_category alignment. "—" where granularity differs.
 </span>
 </div>
 </div>
 )}
 </div>
 );
}

// ---------------------------------------------------------------------------
// ScenarioAccuracyPanel — S484 (OI-SCENARIO-ACCURACY-ENDPOINT)
//
// Fetches GET /api/v1/scenario-history-accuracy?fiscal_period_id={id}
// when a closed or locked fiscal period is active.
//
// Always visible below RoleWeightedExposurePanel — not tab-dependent.
// Strategic lookback panel: "how accurate was our scenario model?"
//
// Empty states:
// - No fiscal period → prompt to open a period
// - Period still open → informational: data available after period closes
// - 404 no actuals → final locked run not found
// - 404 no scenarios → scenario sets not configured
// - error → generic error message
//
// accuracy_rating colour coding:
// within_5pct → emerald (model was excellent)
// within_10pct → amber (model was acceptable)
// outside_10pct → red (model needs recalibration)
//
// best_scenario badge highlights the closest scenario to actuals —
// CFO-facing signal for future planning assumptions.
//
// L2.8 compliant: aggregate only — no position identifiers.
// ---------------------------------------------------------------------------

const ACCURACY_COLOR = {
 within_5pct: "text-[rgb(var(--color-success))]",
 within_10pct: "text-[rgb(var(--color-warning))]",
 outside_10pct: "text-[rgb(var(--color-danger))]",
};

const ACCURACY_BADGE = {
 within_5pct: "bg-[rgb(var(--color-success))]/15 text-[rgb(var(--color-success))]",
 within_10pct: "bg-[rgb(var(--color-warning))]/15 text-[rgb(var(--color-warning))]",
 outside_10pct: "bg-[rgb(var(--color-danger))]/15 text-[rgb(var(--color-danger))]",
};

const ACCURACY_LABEL = {
 within_5pct: "Within 5%",
 within_10pct: "Within 10%",
 outside_10pct: "Outside 10%",
};

function ScenarioAccuracyPanel({ fiscalPeriodId }) {
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState(null);
 const [errorCode, setErrorCode] = useState(null);

 useEffect(() => {
 if (!fiscalPeriodId) {
 setData(null);
 setError(null);
 setErrorCode(null);
 return;
 }

 let cancelled = false;
 setLoading(true);
 setError(null);
 setErrorCode(null);
 setData(null);

 fetch(
 `${API_BASE}/scenario-history-accuracy?fiscal_period_id=${encodeURIComponent(fiscalPeriodId)}`,
 { credentials: "include" }
 )
 .then(async (r) => {
 const json = await r.json().catch(() => ({}));
 if (!r.ok) {
 const code = json?.detail?.error?.code ?? "UNKNOWN";
 if (!cancelled) {
 setErrorCode(code);
 setError(true);
 setLoading(false);
 }
 return;
 }
 if (!cancelled) {
 setData(json);
 setLoading(false);
 }
 })
 .catch(() => {
 if (!cancelled) {
 setErrorCode("NETWORK_ERROR");
 setError(true);
 setLoading(false);
 }
 });

 return () => { cancelled = true; };
 }, [fiscalPeriodId]);

 // ── Panel chrome ─────────────────────────────────────────────────────────
 function PanelShell({ children, subtitle }) {
 return (
 <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
 <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
 <div className="flex items-center gap-2">
 <Activity className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
 <span className="text-sm font-semibold text-[rgb(var(--color-text))]">
 Scenario Accuracy
 </span>
 {subtitle && (
 <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-xs text-[rgb(var(--color-text-muted))]">
 {subtitle}
 </span>
 )}
 </div>
 <span className="text-xs text-[rgb(var(--color-text-muted))]">Closed period · Aggregate only</span>
 </div>
 <div className="p-4">{children}</div>
 </div>
 );
 }

 // ── No fiscal period ──────────────────────────────────────────────────────
 if (!fiscalPeriodId) {
 return (
 <PanelShell>
 <EmptyState
 icon={Info}
 title="No Active Period"
 body="An open fiscal period is required to load scenario accuracy data."
 />
 </PanelShell>
 );
 }

 // ── Loading ───────────────────────────────────────────────────────────────
 if (loading) {
 return (
 <PanelShell>
 <LoadingRow />
 </PanelShell>
 );
 }

 // ── Error states — differentiated by error code ──────────────────────────
 if (error) {
 if (errorCode === "PERIOD_NOT_CLOSED") {
 return (
 <PanelShell>
 <EmptyState
 icon={Info}
 title="Period Still Open"
 body="Scenario accuracy data is available once the fiscal period closes. Check back after period-end."
 />
 </PanelShell>
 );
 }
 if (errorCode === "NO_ACTUALS_FOUND") {
 return (
 <PanelShell>
 <EmptyState
 icon={AlertTriangle}
 title="No Locked Final Run Found"
 body="A locked final calculation run is required to compute actuals. Contact your Comp Admin."
 />
 </PanelShell>
 );
 }
 if (errorCode === "NO_SCENARIO_DATA") {
 return (
 <PanelShell>
 <EmptyState
 icon={AlertTriangle}
 title="No Scenario Data for This Period"
 body="Scenario sets were not configured for this period. Accuracy comparison is unavailable."
 />
 </PanelShell>
 );
 }
 return (
 <PanelShell>
 <p className="text-xs text-[rgb(var(--color-danger))]">Unable to load scenario accuracy data.</p>
 </PanelShell>
 );
 }

 // ── No data ───────────────────────────────────────────────────────────────
 if (!data || !data.scenarios || data.scenarios.length === 0) {
 return (
 <PanelShell>
 <EmptyState
 icon={Activity}
 title="No Accuracy Data Available"
 body="No scenario accuracy data found for this period."
 />
 </PanelShell>
 );
 }

 const { scenarios, summary, period_label, period_status, actual_total_payout, currency_code, best_scenario, position_count } = data;
 const cur = currency_code || "USD";

 return (
 <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">

 {/* Header */}
 <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
 <div className="flex items-center gap-2">
 <Activity className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
 <span className="text-sm font-semibold text-[rgb(var(--color-text))]">
 Scenario Accuracy
 </span>
 <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-xs text-[rgb(var(--color-text-muted))]">
 {period_label}
 </span>
 <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${
 period_status === "locked"
 ? "bg-[rgb(var(--color-primary))]/15 text-[rgb(var(--color-primary))]"
 : "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]"
 }`}>
 {period_status}
 </span>
 </div>
 <span className="text-xs text-[rgb(var(--color-text-muted))]">Closed period · Aggregate only</span>
 </div>

 {/* KPI strip — actual total + best scenario */}
 <div className="grid grid-cols-3 gap-3 border-b border-[rgb(var(--color-border))] px-4 py-3">
 <div>
 <div className="text-xs text-[rgb(var(--color-text-muted))]">Actual Total Payout</div>
 <div className="mt-0.5 text-sm font-bold text-[rgb(var(--color-text))]">
 {fmt(parseFloat(actual_total_payout), cur)}
 </div>
 <div className="text-xs text-[rgb(var(--color-text-muted))]">{position_count.toLocaleString()} positions</div>
 </div>
 <div>
 <div className="text-xs text-[rgb(var(--color-text-muted))]">Closest Scenario</div>
 <div className="mt-0.5 flex items-center gap-1.5">
 <span className="text-sm font-bold text-[rgb(var(--color-text))]">
 {best_scenario || "—"}
 </span>
 {summary?.accuracy_rating && (
 <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ACCURACY_BADGE[summary.accuracy_rating] ?? ACCURACY_BADGE.outside_10pct}`}>
 {ACCURACY_LABEL[summary.accuracy_rating] ?? summary.accuracy_rating}
 </span>
 )}
 </div>
 <div className="text-xs text-[rgb(var(--color-text-muted))]">
 {summary?.closest_variance_pct
 ? `${parseFloat(summary.closest_variance_pct) >= 0 ? "+" : ""}${parseFloat(summary.closest_variance_pct).toFixed(2)}% vs actual`
 : "—"}
 </div>
 </div>
 <div>
 <div className="text-xs text-[rgb(var(--color-text-muted))]">Model Reliability</div>
 <div className={`mt-0.5 text-sm font-bold ${ACCURACY_COLOR[summary?.accuracy_rating] ?? "text-[rgb(var(--color-text-muted))]"}`}>
 {ACCURACY_LABEL[summary?.accuracy_rating] ?? "—"}
 </div>
 <div className="text-xs text-[rgb(var(--color-text-muted))]">Best scenario vs actuals</div>
 </div>
 </div>

 {/* Scenario comparison table */}
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-[rgb(var(--color-border))]">
 {["Scenario", "Projected", "Actual", "Variance", "Variance %", "Accuracy"].map((h) => (
 <th
 key={h}
 className={`px-3 py-2 font-medium text-[rgb(var(--color-text-muted))] whitespace-nowrap ${
 h === "Scenario" ? "text-left" : "text-right"
 }`}
 >
 {h}
 </th>
 ))}
 </tr>
 </thead>
 <tbody>
 {scenarios.map((s) => {
 const varPct = parseFloat(s.variance_pct);
 const varClass = ACCURACY_COLOR[s.accuracy_rating] ?? "text-[rgb(var(--color-text-muted))]";
 const isBest = s.scenario_code === best_scenario;

 return (
 <tr
 key={s.scenario_code}
 className={`border-b border-[rgb(var(--color-border))] last:border-0 ${
 isBest ? "bg-[rgb(var(--color-surface-muted))] /40" : "hover:bg-[rgb(var(--color-surface-muted))]"
 }`}
 >
 <td className="px-3 py-2 whitespace-nowrap">
 <div className="flex items-center gap-1.5">
 <span className="font-semibold text-[rgb(var(--color-text))]">
 {s.percentile_label || s.scenario_code}
 </span>
 {isBest && (
 <span className="rounded-full bg-[rgb(var(--color-primary))]/15 px-1.5 py-0.5 text-[9px] font-semibold text-[rgb(var(--color-primary))]">
 BEST
 </span>
 )}
 </div>
 </td>
 <td className="px-3 py-2 text-right text-[rgb(var(--color-text-muted))] tabular-nums whitespace-nowrap">
 {fmt(parseFloat(s.projected_total_payout), cur)}
 </td>
 <td className="px-3 py-2 text-right font-medium text-[rgb(var(--color-text))] tabular-nums whitespace-nowrap">
 {fmt(parseFloat(s.actual_total_payout), cur)}
 </td>
 <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${varClass}`}>
 {varPct >= 0 ? "+" : ""}{fmt(Math.abs(parseFloat(s.variance)), cur)}
 </td>
 <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${varClass}`}>
 {fmtPctSigned(varPct)}
 </td>
 <td className="px-3 py-2 text-right whitespace-nowrap">
 <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ACCURACY_BADGE[s.accuracy_rating] ?? ACCURACY_BADGE.outside_10pct}`}>
 {ACCURACY_LABEL[s.accuracy_rating] ?? s.accuracy_rating}
 </span>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>

 {/* Footer — model calibration note */}
 <div className="border-t border-[rgb(var(--color-border))] px-4 py-2">
 <p className="text-[10px] text-[rgb(var(--color-text-muted))]">
 Variance = Actual − Projected. Positive = actual spend exceeded projection (model underestimated).
 Accuracy bands: Within 5% · Within 10% · Outside 10%.
 Source: locked final calculation run vs scenario projections for {period_label}.
 </p>
 </div>
 </div>
 );
}

// ---------------------------------------------------------------------------
// RoleWeightedExposurePanel — S483 Phase C (OI-ROLE-WEIGHTED-EXPOSURE)
//
// Fetches GET /api/v1/scenario-exposure/by-role when selectedRunId is set.
// Always visible below AopHcDeltaPanel — not tab-dependent.
// Re-fetches when selectedRunId changes.
//
// Header: "Actual Payout by Role" — NOT "P50/P75/P90 by Role".
// These are run-specific engine-computed actuals, not scenario projections.
//
// L2.8 compliant: server returns aggregate by role_id — no position identifiers.
//
// Table columns:
// Role | Positions | Actual Payout | Per Head | AOP Budget | vs AOP
//
// vs AOP: % variance = variance_vs_aop / aop_budget
// positive (over budget) → red
// negative (under budget) → green
// aop_budget "0.0000" → show "—"
//
// Footer: summary totals from server summary object.
// Empty state when no run selected directs user to Actuals tab.
// ---------------------------------------------------------------------------

function RoleWeightedExposurePanel({ fiscalPeriodId, selectedRunId }) {
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState(null);

 useEffect(() => {
 if (!selectedRunId) {
 setData(null);
 setError(null);
 return;
 }

 let cancelled = false;
 setLoading(true);
 setError(null);
 setData(null);

 const params = new URLSearchParams({ calculation_run_id: selectedRunId });
 if (fiscalPeriodId != null) {
 params.set("fiscal_period_id", String(fiscalPeriodId));
 }

 fetch(`${API_BASE}/scenario-exposure/by-role?${params.toString()}`, {
 credentials: "include",
 })
 .then(async (r) => {
 if (!r.ok) throw new Error(`${r.status}`);
 return r.json();
 })
 .then((json) => {
 if (!cancelled) {
 setData(json);
 setLoading(false);
 }
 })
 .catch(() => {
 if (!cancelled) {
 setError(true);
 setLoading(false);
 }
 });

 return () => { cancelled = true; };
 }, [selectedRunId, fiscalPeriodId]);

 // ── Panel chrome shared across all states ────────────────────────────────
 function PanelShell({ children }) {
 return (
 <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
 <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
 <div className="flex items-center gap-2">
 <TrendingUp className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
 <span className="text-sm font-semibold text-[rgb(var(--color-text))]">
 Actual Payout by Role
 </span>
 {selectedRunId && (
 <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-xs text-[rgb(var(--color-text-muted))]">
 Engine-computed · Run {shortId(selectedRunId)}
 </span>
 )}
 </div>
 <span className="text-xs text-[rgb(var(--color-text-muted))]">Aggregate only</span>
 </div>
 <div className="p-4">{children}</div>
 </div>
 );
 }

 // ── No run selected ──────────────────────────────────────────────────────
 if (!selectedRunId) {
 return (
 <PanelShell>
 <EmptyState
 icon={Activity}
 title="No Calculation Run Selected"
 body="Select a calculation run in the Actuals tab to see role-level payout breakdown."
 />
 </PanelShell>
 );
 }

 // ── Loading ──────────────────────────────────────────────────────────────
 if (loading) {
 return (
 <PanelShell>
 <LoadingRow />
 </PanelShell>
 );
 }

 // ── Error ────────────────────────────────────────────────────────────────
 if (error) {
 return (
 <PanelShell>
 <p className="text-xs text-[rgb(var(--color-danger))]">Unable to load role payout data.</p>
 </PanelShell>
 );
 }

 // ── No rows returned ─────────────────────────────────────────────────────
 if (!data || !data.roles || data.roles.length === 0) {
 return (
 <PanelShell>
 <EmptyState
 icon={Users}
 title="No Payout Data for This Run"
 body="Role-level payout data is not available for the selected run."
 />
 </PanelShell>
 );
 }

 const { roles, role_count, summary } = data;
 // Currency from first role — all rows share the same run's currency
 const cur = roles[0]?.currency_code || "USD";

 return (
 <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">

 {/* Header */}
 <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
 <div className="flex items-center gap-2">
 <TrendingUp className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
 <span className="text-sm font-semibold text-[rgb(var(--color-text))]">
 Actual Payout by Role
 </span>
 <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-xs text-[rgb(var(--color-text-muted))]">
 Engine-computed · Run {shortId(selectedRunId)}
 </span>
 </div>
 <span className="text-xs text-[rgb(var(--color-text-muted))]">Aggregate only</span>
 </div>

 {/* Table */}
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-[rgb(var(--color-border))]">
 {["Role", "Positions", "Actual Payout", "Per Head", "AOP Budget", "vs AOP"].map((h) => (
 <th
 key={h}
 className={`px-3 py-2 font-medium text-[rgb(var(--color-text-muted))] whitespace-nowrap ${
 h === "Role" ? "text-left" : "text-right"
 }`}
 >
 {h}
 </th>
 ))}
 </tr>
 </thead>
 <tbody>
 {roles.map((role) => {
 const hasAop = role.aop_budget !== "0.0000";
 const variancePct =
 hasAop
 ? (parseFloat(role.variance_vs_aop) / parseFloat(role.aop_budget)) * 100
 : null;
 const varianceClass =
 variancePct == null
 ? "text-[rgb(var(--color-text-muted))]"
 : variancePct > 0
 ? "text-[rgb(var(--color-danger))]"
 : "text-[rgb(var(--color-success))]";

 return (
 <tr
 key={role.role_id}
 className="border-b border-[rgb(var(--color-border))] last:border-0 hover:bg-[rgb(var(--color-surface-muted))]"
 >
 <td className="px-3 py-2 whitespace-nowrap">
 <span className="font-medium text-[rgb(var(--color-text))]">
 {role.role_name || "—"}
 </span>
 {role.role_code && (
 <span className="ml-1.5 text-[rgb(var(--color-text-muted))]">{role.role_code}</span>
 )}
 </td>
 <td className="px-3 py-2 text-right text-[rgb(var(--color-text-muted))] tabular-nums whitespace-nowrap">
 {role.position_count.toLocaleString()}
 </td>
 <td className="px-3 py-2 text-right font-medium text-[rgb(var(--color-text))] tabular-nums whitespace-nowrap">
 {fmt(parseFloat(role.actual_total_payout), cur)}
 </td>
 <td className="px-3 py-2 text-right text-[rgb(var(--color-text-muted))] tabular-nums whitespace-nowrap">
 {fmt(parseFloat(role.payout_per_head), cur)}
 </td>
 <td className="px-3 py-2 text-right text-[rgb(var(--color-text-muted))] tabular-nums whitespace-nowrap">
 {hasAop ? fmt(parseFloat(role.aop_budget), cur) : "—"}
 </td>
 <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${varianceClass}`}>
 {variancePct == null ? "—" : fmtPctSigned(variancePct)}
 </td>
 </tr>
 );
 })}
 </tbody>

 {/* Footer — summary totals from server */}
 <tfoot>
 <tr className="border-t-2 border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] /50">
 <td className="px-3 py-2 text-xs font-semibold text-[rgb(var(--color-text-muted))] whitespace-nowrap">
 Total ({role_count} role{role_count !== 1 ? "s" : ""})
 </td>
 <td className="px-3 py-2 text-right text-[rgb(var(--color-text-muted))]">—</td>
 <td className="px-3 py-2 text-right text-xs font-semibold text-[rgb(var(--color-text))] tabular-nums whitespace-nowrap">
 {fmt(parseFloat(summary.total_actual_payout), cur)}
 </td>
 <td className="px-3 py-2 text-right text-[rgb(var(--color-text-muted))]">—</td>
 <td className="px-3 py-2 text-right text-xs font-semibold text-[rgb(var(--color-text-muted))] tabular-nums whitespace-nowrap">
 {summary.total_aop_budget !== "0.0000"
 ? fmt(parseFloat(summary.total_aop_budget), cur)
 : "—"}
 </td>
 <td className="px-3 py-2 text-right text-xs font-semibold text-[rgb(var(--color-text-muted))] tabular-nums whitespace-nowrap">
 {fmt(parseFloat(summary.total_variance_vs_aop), cur)}
 </td>
 </tr>
 </tfoot>
 </table>
 </div>
 </div>
 );
}

// ---------------------------------------------------------------------------
// ExposureMonitor — main component
//
// S608 MAKEOVER — 3-section CFO layout:
//
// SECTION 1 — SPEND PROJECTION (leads)
// Run Exposure Analysis — elevated primary CTA, inline (no modal hunt)
// KPI band — VC Budget (wired S608), P50/P75/P90, Budget Variance at P90
// Distribution tabs — Actuals | Typical SaaS | High Performance | Developing | Custom
// Bell curve + Payout exposure curve
//
// SECTION 2 — FORECAST QUALITY GATE (collapsible, defaults open)
// Forecast Readiness card
// Header metadata strip
// Population context + Driver bullets
//
// SECTION 3 — SCENARIO ACCURACY (post-close)
// ScenarioAccuracyPanel — clean empty state when period open (no "Period Still Open" junk)
//
// REMOVED from this component (moved to Plan Governance / Period Dashboard):
// PlanVsLoadedPanel → Plan Governance
// AopHcDeltaPanel → Plan Governance
// RoleWeightedExposurePanel → Period Dashboard
//
// OI-FINANCE-EXPOSURE-MONITOR-BUDGET-BLANK FIXED:
// allPlanInputs prop from shell → filter approved for fiscalPeriodId
// → variable_comp_budget → KPIBand budget prop
// Falls back to exposure?.summary?.budget (legacy) when prop unavailable.
// ---------------------------------------------------------------------------

export default function ExposureMonitor({ allPlanInputs, fiscalPeriodId, wizardSessionId }) {
 // Data state
 const [runs, setRuns] = useState([]);
 const [runsLoading, setRunsLoading] = useState(true);
 const [selectedRunId, setSelectedRunId] = useState("");
 const [exposure, setExposure] = useState(null);
 const [expLoading, setExpLoading] = useState(false);
 const [expError, setExpError] = useState(null);
 const [population, setPopulation] = useState(null);
 const [popLoading, setPopLoading] = useState(true);
 const [popError, setPopError] = useState(null);
 const [readiness, setReadiness] = useState(null);
 const [readinessLoading, setReadinessLoading] = useState(true);
 const [readinessError, setReadinessError] = useState(null);
 const [period, setPeriod] = useState(null);

 // EM-01: aopVersion from approved financial-plan-inputs
 const [aopPlanVersion, setAopPlanVersion] = useState(null);

 // EM-04: aboveQuotaPct lifted from LeftPanel
 const [aboveQuotaPct, setAboveQuotaPct] = useState(null);

 // S485: By Role toggle for Actuals tab
 const [byRole, setByRole] = useState(false);
 const [byRoleExposure, setByRoleExposure] = useState(null);
 const [byRoleLoading, setByRoleLoading] = useState(false);
 const [expandedPercentiles, setExpandedPercentiles] = useState({});

 // S572-T1f: Scenario sets + inline Run Exposure state
 const [scenarioSets, setScenarioSets] = useState([]);
 const [runExposureSetId, setRunExposureSetId] = useState("");
 const [runExposureSubmitting, setRunExposureSubmitting] = useState(false);
 const [runExposureError, setRunExposureError] = useState(null);
 const [runExposureSuccess, setRunExposureSuccess] = useState(null);

 // Tab + custom distribution state
 const [activeTab, setActiveTab] = useState("actuals");
 const [customMean, setCustomMean] = useState("85");
 const [customSd, setCustomSd] = useState("25");

 // Section 2 — Forecast Quality Gate collapsible (defaults open)
 const [qualityGateOpen, setQualityGateOpen] = useState(true);

 // Fetch period when fiscalPeriodId not provided as prop (legacy fallback)
 useEffect(() => {
 if (fiscalPeriodId) return; // shell provides it — no internal fetch needed
 fetch(`${API_BASE}/fiscal-periods?period_status=open`, { credentials: "include" })
 .then((r) => r.ok ? r.json() : null)
 .then((d) => setPeriod(d?.data?.[0] ?? null))
 .catch(() => {});
 }, [fiscalPeriodId]);

 // Sync period from prop when shell provides fiscalPeriodId
 useEffect(() => {
 if (!fiscalPeriodId) return;
 setPeriod((prev) => prev?.fiscal_period_id === fiscalPeriodId ? prev : { fiscal_period_id: fiscalPeriodId });
 }, [fiscalPeriodId]);

 const activePeriodId = period?.fiscal_period_id ?? null;

 // S572-T1f: Fetch approved scenario sets for run exposure
 useEffect(() => {
 if (!activePeriodId) return;
 fetch(
 `${API_BASE}/scenario-sets?fiscal_period_id=${encodeURIComponent(activePeriodId)}`,
 { credentials: "include" }
 )
 .then((r) => r.ok ? r.json() : { scenario_sets: [] })
 .then((d) => {
 const approved = (d?.scenario_sets ?? []).filter((s) => s.approved_by);
 setScenarioSets(approved);
 if (approved.length > 0) setRunExposureSetId(approved[0].scenario_set_id);
 })
 .catch(() => {});
 }, [activePeriodId]);

 // EM-01: aopVersion from financial-plan-inputs prop or internal fetch
 useEffect(() => {
 // If shell provides allPlanInputs, derive aopVersion client-side — no fetch
 if (allPlanInputs && allPlanInputs.length > 0 && activePeriodId) {
 const approved = allPlanInputs.filter(
 (i) => i.fiscal_period_id === activePeriodId && i.approval_status === "approved" && i.is_active
 );
 setAopPlanVersion(approved.length > 0 ? `v${approved[0].version} Approved` : null);
 return;
 }
 if (!activePeriodId) return;
 fetch(
 `${API_BASE}/financial-plan-inputs?fiscal_period_id=${encodeURIComponent(activePeriodId)}`,
 { credentials: "include" }
 )
 .then((r) => r.ok ? r.json() : null)
 .then((d) => {
 const approved = (d?.data ?? []).filter((i) => i.approval_status === "approved");
 setAopPlanVersion(approved.length > 0 ? `v${approved[0].version} Approved` : null);
 })
 .catch(() => setAopPlanVersion(null));
 }, [activePeriodId, allPlanInputs]);

 // Fetch completed calculation runs
 useEffect(() => {
 setRunsLoading(true);
 fetch(`${API_BASE}/calculation-runs`, { credentials: "include" })
 .then((r) => r.ok ? r.json() : { calculation_runs: [] })
 .then((d) => {
 const completed = (d?.calculation_runs ?? []).filter((r) => r.run_status === "completed");
 setRuns(completed);
 if (completed.length > 0) setSelectedRunId(completed[0].calculation_run_id || completed[0].id);
 })
 .catch(() => {})
 .finally(() => setRunsLoading(false));
 }, []);

 // Fetch exposure when run selected
 useEffect(() => {
 if (!selectedRunId) return;
 setExpLoading(true);
 setExpError(null);
 setExposure(null);
 fetch(
 `${API_BASE}/scenario-exposure?calculation_run_id=${encodeURIComponent(selectedRunId)}`,
 { credentials: "include" }
 )
 .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
 .then(setExposure)
 .catch((e) => setExpError(e.message))
 .finally(() => setExpLoading(false));
 }, [selectedRunId]);

 // S485: By Role exposure
 useEffect(() => {
 if (!byRole || !selectedRunId) { setByRoleExposure(null); setExpandedPercentiles({}); return; }
 let cancelled = false;
 setByRoleLoading(true);
 setByRoleExposure(null);
 fetch(
 `${API_BASE}/scenario-exposure?calculation_run_id=${encodeURIComponent(selectedRunId)}&by_role=true`,
 { credentials: "include" }
 )
 .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
 .then((json) => { if (!cancelled) setByRoleExposure(json); })
 .catch(() => { if (!cancelled) setByRoleExposure(null); })
 .finally(() => { if (!cancelled) setByRoleLoading(false); });
 return () => { cancelled = true; };
 }, [byRole, selectedRunId]);

 // Fetch population breakdown
 useEffect(() => {
 setPopLoading(true);
 fetch(`${API_BASE}/population-breakdown`, { credentials: "include" })
 .then((r) => r.ok ? r.json() : null)
 .then(setPopulation)
 .catch((e) => setPopError(e.message))
 .finally(() => setPopLoading(false));
 }, []);

 // Fetch forecast readiness
 useEffect(() => {
 setReadinessLoading(true);
 fetch(`${API_BASE}/finance-readiness`, { credentials: "include" })
 .then((r) => r.ok ? r.json() : null)
 .then(setReadiness)
 .catch((e) => setReadinessError(e.message))
 .finally(() => setReadinessLoading(false));
 }, []);

 // EM-04: stable callback
 const handleAboveQuotaPct = useCallback((val) => setAboveQuotaPct(val), []);

 // S572-T1f: Run Exposure Analysis handler (inline — no modal)
 const handleRunExposure = async () => {
 if (!selectedRunId || !runExposureSetId) return;
 setRunExposureSubmitting(true);
 setRunExposureError(null);
 setRunExposureSuccess(null);
 try {
 const res = await fetch(`${API_BASE}/scenario-exposure/compute`, {
 method: "POST",
 credentials: "include",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify({
 calculation_run_id: selectedRunId,
 scenario_set_id: runExposureSetId,
 fiscal_period_id: activePeriodId,
 }),
 });
 const json = await res.json().catch(() => ({}));
 if (!res.ok) {
 const err = json?.error ?? json?.detail?.error;
 setRunExposureError(err?.message ?? err?.recovery_action ?? `Error ${res.status}`);
 return;
 }
 setRunExposureSuccess("Exposure analysis computed successfully. P50/P75/P90 projections updated.");
 setExposure(null);
 setExpLoading(true);
 fetch(
 `${API_BASE}/scenario-exposure?calculation_run_id=${encodeURIComponent(selectedRunId)}`,
 { credentials: "include" }
 )
 .then((r) => r.ok ? r.json() : null)
 .then(setExposure)
 .catch(() => {})
 .finally(() => setExpLoading(false));
 } catch (e) {
 setRunExposureError(e.message || "Network error.");
 } finally {
 setRunExposureSubmitting(false);
 }
 };

 // ── Derived values ─────────────────────────────────────────────────────────

 const totalHC = population?.total_headcount ?? null;
 const currency = exposure?.currency_code ?? "USD";

 // OI-FINANCE-EXPOSURE-MONITOR-BUDGET-BLANK — S608 fix:
 // Source 1: allPlanInputs prop (shell-level, already fetched) — preferred
 // Source 2: exposure?.summary?.budget (legacy, requires exposure run to exist)
 const approvedPlanInput = allPlanInputs && activePeriodId
 ? (allPlanInputs ?? []).find(
 (i) => i.fiscal_period_id === activePeriodId
 && i.approval_status === "approved"
 && i.is_active
 ) ?? null
 : null;
 const budget = approvedPlanInput
 ? String(approvedPlanInput.variable_comp_budget)
 : (exposure?.summary?.budget ?? null);

 const p50 = exposure?.summary?.p50_projected_payout ?? null;
 const p75 = exposure?.summary?.p75_projected_payout ?? null;
 const p90 = exposure?.summary?.p90_projected_payout ?? null;
 const headroomP50 = exposure?.summary?.budget_headroom_at_p50 ?? null;
 const headroomP75 = exposure?.summary?.budget_headroom_at_p75 ?? null;
 const headroomP90 = exposure?.summary?.budget_headroom_at_p90 ?? null;
 const aopVersion = aopPlanVersion;
 const planVersion = exposure?.scenario_set_name ?? null;
 const hcSnapshot = population ? "As of data load" : null;

 const activeTabDef = TABS.find((t) => t.id === activeTab) ?? TABS[0];
 const noHC = !popLoading && (!population?.population_breakdown || population.population_breakdown.length === 0);
 const noRun = !runsLoading && runs.length === 0;
 const noExposure = !expLoading && !exposure && activeTab === "actuals";

 // Run Exposure readiness
 const hasApprovedScenarioSet = scenarioSets.length > 0;
 const canRunExposure = selectedRunId && hasApprovedScenarioSet && !runExposureSubmitting;

 return (
 <div className="space-y-6">

 {/* ═══════════════════════════════════════════════════════════════════════
 SECTION 1 — SPEND PROJECTION
 Primary CFO question: "What will I spend on variable comp this period?"
 Lead with the answer. Run Exposure is the primary CTA — elevated here.
 ═══════════════════════════════════════════════════════════════════════ */}

 {/* ── Run Exposure Analysis — elevated primary CTA ── */}
 <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
 <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[rgb(var(--color-border))] px-4 py-3">
 <div>
 <h2 className="text-sm font-bold text-[rgb(var(--color-text))]">Run Exposure Analysis</h2>
 <p className="mt-0.5 text-xs text-[rgb(var(--color-text-muted))]">
 Compute P50 / P75 / P90 spend projections against the approved scenario set for this period.
 </p>
 </div>
 <button
 onClick={handleRunExposure}
 disabled={!canRunExposure}
 className="flex items-center gap-2 rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
 >
 <TrendingUp className="h-3.5 w-3.5" />
 {runExposureSubmitting ? "Computing…" : "Compute Exposure"}
 </button>
 </div>

 <div className="px-4 py-3 space-y-3">
 {/* Run selector + scenario set inline */}
 <div className="grid gap-3 sm:grid-cols-2">
 <div>
 <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">Calculation Run</label>
 {runsLoading ? (
 <div className="h-8 animate-pulse rounded-xl bg-[rgb(var(--color-surface-muted))]" />
 ) : (
 <RunSelector
 runs={runs}
 selectedRunId={selectedRunId}
 onSelect={(id) => {
 setSelectedRunId(id);
 setByRoleExposure(null);
 setExpandedPercentiles({});
 setRunExposureSuccess(null);
 }}
 />
 )}
 </div>
 <div>
 <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">Scenario Set</label>
 {!hasApprovedScenarioSet ? (
 <div className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-xs text-[rgb(var(--color-text-muted))]">
 No approved scenario set — pending approval.
 </div>
 ) : (
 <select
 value={runExposureSetId}
 onChange={(e) => setRunExposureSetId(e.target.value)}
 className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-xs"
 >
 {scenarioSets.map((s) => (
 <option key={s.scenario_set_id} value={s.scenario_set_id}>
 {s.scenario_set_name || s.scenario_set_code} (approved)
 </option>
 ))}
 </select>
 )}
 </div>
 </div>

 {/* Feedback */}
 {runExposureSuccess && (
 <div className="rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 px-3 py-2 text-xs text-[rgb(var(--color-success))]">
 ✓ {runExposureSuccess}
 </div>
 )}
 {runExposureError && (
 <div className="rounded-lg border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-xs text-[rgb(var(--color-danger))]">
 {runExposureError}
 </div>
 )}
 </div>
 </div>

 {/* ── KPI Band — VC Budget now wired (OI-FINANCE-EXPOSURE-MONITOR-BUDGET-BLANK) ── */}
 <KPIBand
 budget={budget}
 p50={p50}
 p75={p75}
 p90={p90}
 headroomP50={headroomP50}
 headroomP75={headroomP75}
 headroomP90={headroomP90}
 currency={currency}
 />

 {/* ── Distribution tabs + visualizations ── */}
 <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">

 {/* Pattern selector cards — each distribution scenario rendered as a card */}
 <div className="border-b border-[rgb(var(--color-border))] px-4 py-3">
 <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
 {TABS.map((tab) => {
 const isActive = activeTab === tab.id;
 const accentMap = {
 primary: "--color-primary",
 success: "--color-success",
 warning: "--color-warning",
 danger: "--color-danger",
 "text-muted": "--color-text-muted",
 };
 const accentVar = accentMap[tab.accent] ?? "--color-primary";
 return (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id)}
 className={`rounded-xl border p-3 text-left transition-all ${
 isActive
 ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/8 shadow-sm"
 : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] hover:border-[rgb(var(--color-primary))]/40 hover:bg-[rgb(var(--color-surface-muted))]"
 }`}
 >
 <div className="mb-1 flex items-center justify-between">
 <span className={`text-xs font-bold ${isActive ? "text-[rgb(var(--color-primary))]" : "text-[rgb(var(--color-text))]"}`}>
 {tab.label}
 </span>
 {tab.id === "actuals" && (
 <span className="rounded-full bg-[rgb(var(--color-success))]/15 px-1.5 py-px text-[9px] font-semibold text-[rgb(var(--color-success))]">Live</span>
 )}
 </div>
 <p className="text-[10px] leading-3.5 text-[rgb(var(--color-text-muted))]">{tab.desc}</p>
 <p className={`mt-1.5 text-[10px] font-semibold`} style={{ color: `rgb(var(${accentVar}))` }}>{tab.stat}</p>
 </button>
 );
 })}
 </div>
 </div>


 <div className="p-4">

 {/* Actuals tab — By Role toggle */}
 {activeTab === "actuals" && (
 <div className="mb-4 flex items-center justify-end gap-2">
 <button
 onClick={() => { setByRole((prev) => !prev); setExpandedPercentiles({}); }}
 className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
 byRole
 ? "border-[rgb(var(--color-primary))]/40 bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]"
 : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-muted))] hover:border-[rgb(var(--color-border))]"
 }`}
 >
 <Users className="h-3 w-3" />
 By Role
 </button>
 </div>
 )}

 {/* S485: By Role percentile breakdown */}
 {activeTab === "actuals" && byRole && selectedRunId && (
 <div className="mb-4 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
 <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-3 py-2">
 <span className="text-xs font-semibold text-[rgb(var(--color-text))]">Scenario Projections by Role</span>
 <span className="text-xs text-[rgb(var(--color-text-muted))]">Source: Path B engine · role_id dimension</span>
 </div>
 {byRoleLoading && <div className="p-3"><LoadingRow /></div>}
 {!byRoleLoading && (!byRoleExposure || !byRoleExposure.percentiles?.length) && (
 <div className="p-3 text-xs text-[rgb(var(--color-text-muted))]">No role-level projection data available for this run. Role breakdown requires a Path B calculation run with W-27.</div>
 )}
 {!byRoleLoading && byRoleExposure?.percentiles?.length > 0 && (
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-[rgb(var(--color-border))]">
 {["Scenario", "Projected Payout", "vs Budget", ""].map((h) => (
 <th key={h} className="px-3 py-2 text-left font-medium text-[rgb(var(--color-text-muted))]">{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {byRoleExposure.percentiles.map((p) => {
 const isExpanded = !!expandedPercentiles[p.scenario_definition_id];
 const roles = p.roles ?? [];
 return [
 <tr
 key={p.scenario_definition_id}
 className="border-b border-[rgb(var(--color-border))] cursor-pointer hover:bg-[rgb(var(--color-surface))]"
 onClick={() => setExpandedPercentiles((prev) => ({ ...prev, [p.scenario_definition_id]: !prev[p.scenario_definition_id] }))}
 >
 <td className="px-3 py-2 font-semibold text-[rgb(var(--color-text))]">{p.percentile_label || p.scenario_code}</td>
 <td className="px-3 py-2 tabular-nums text-[rgb(var(--color-text-muted))]">{fmt(parseFloat(p.projected_total_payout), byRoleExposure.currency_code || "USD")}</td>
 <td className={`px-3 py-2 tabular-nums ${parseFloat(p.variance_pct) > 0 ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-success))]"}`}>{fmtPctSigned(parseFloat(p.variance_pct))}</td>
 <td className="px-3 py-2 text-right">
 {roles.length > 0 ? (isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))] inline" /> : <ChevronDown className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))] inline" />) : <span className="text-[rgb(var(--color-text-muted))] text-[10px]">no roles</span>}
 </td>
 </tr>,
 isExpanded && roles.map((role) => (
 <tr key={`${p.scenario_definition_id}-${role.role_id}`} className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]/50">
 <td className="py-1.5 pl-8 pr-3 text-[rgb(var(--color-text-muted))]">{role.role_code || `Role ${role.role_id}`}{role.role_name ? <span className="ml-1 text-[rgb(var(--color-text-muted))]">· {role.role_name}</span> : null}</td>
 <td className="px-3 py-1.5 tabular-nums text-[rgb(var(--color-text-muted))]">{fmt(parseFloat(role.projected_payout), byRoleExposure.currency_code || "USD")}</td>
 <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">—</td>
 <td className="px-3 py-1.5 text-right text-[rgb(var(--color-text-muted))]">{role.position_count} pos</td>
 </tr>
 )),
 ];
 })}
 </tbody>
 </table>
 </div>
 )}
 </div>
 )}

 {/* Custom tab inputs */}
 {activeTab === "custom" && (
 <div className="mb-4 grid gap-3 sm:grid-cols-2 rounded-xl border border-[rgb(var(--color-border))] p-3">
 <div>
 <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">Average Attainment %</label>
 <input type="number" value={customMean} onChange={(e) => setCustomMean(e.target.value)} min="0" max="200" placeholder="e.g. 85" className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-sm" />
 </div>
 <div>
 <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">Performance Spread %</label>
 <input type="number" value={customSd} onChange={(e) => setCustomSd(e.target.value)} min="1" max="100" placeholder="e.g. 25" className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-sm" />
 </div>
 </div>
 )}

 {/* Empty states */}
 {noHC && <EmptyState icon={Users} title="No Loaded Workforce Available" body="Contact your Comp Admin to load positions before exposure can be modeled." />}
 {!noHC && noRun && activeTab === "actuals" && <EmptyState icon={Activity} title="No Completed Calculation Run Found" body="A completed calculation run is required to show actuals. Distribution tabs are available while you wait." />}
 {!noHC && !noRun && noExposure && <EmptyState icon={TrendingUp} title="Exposure Data Unavailable" body="Unable to load scenario exposure for this run. Try selecting a different run or use Run Exposure Analysis above." />}

 {/* Two-column layout — 65% left / 35% right */}
 {!noHC && (
 <div className="flex gap-4">
 <div className="min-w-0 flex-[65]">
 {activeTab === "actuals" && expLoading ? <LoadingRow /> : (
 <LeftPanel
 tab={activeTabDef}
 exposure={exposure}
 budget={budget}
 currency={currency}
 customMean={customMean}
 customSd={customSd}
 onAboveQuotaPct={handleAboveQuotaPct}
 />
 )}
 </div>
 <div className="flex-[35] space-y-3 min-w-0">
 <PopulationContextTable population={population?.population_breakdown} loading={popLoading} error={popError} />
 <DriverBullets exposure={exposure} population={population} aboveQuotaPct={aboveQuotaPct} />
 </div>
 </div>
 )}
 </div>
 </div>

 {/* ═══════════════════════════════════════════════════════════════════════
 SECTION 2 — FORECAST QUALITY GATE
 CFO question: "How confident should I be in these projections?"
 Collapsible — ops-confident CFOs collapse it; new-period CFOs keep it open.
 ═══════════════════════════════════════════════════════════════════════ */}

 <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
 {/* Collapsible header */}
 <button
 onClick={() => setQualityGateOpen((v) => !v)}
 className="flex w-full items-center justify-between px-4 py-3 text-left"
 >
 <div className="flex items-center gap-2">
 <Activity className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
 <span className="text-sm font-bold text-[rgb(var(--color-text))]">Forecast Quality Gate</span>
 {readiness?.readiness_score != null && (
 <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
 readiness.readiness_score >= 80
 ? "bg-[rgb(var(--color-success))]/15 text-[rgb(var(--color-success))]"
 : "bg-[rgb(var(--color-warning))]/15 text-[rgb(var(--color-warning))]"
 }`}>
 {readiness.readiness_score}% Ready
 </span>
 )}
 </div>
 {qualityGateOpen
 ? <ChevronUp className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
 : <ChevronDown className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
 }
 </button>

 {qualityGateOpen && (
 <div className="border-t border-[rgb(var(--color-border))] px-4 py-4 space-y-4">

 {/* Forecast Readiness card — inline (not standalone card, already inside panel) */}
 <ForecastReadinessCard readiness={readiness} loading={readinessLoading} error={readinessError} />

 {/* Metadata strip */}
 <HeaderMetadataStrip
 aopVersion={aopVersion}
 runId={selectedRunId}
 planVersion={planVersion}
 hcSnapshot={hcSnapshot}
 />

 {/* Summary chips */}
 <SummaryChips
 periodLabel={period?.period_label ?? period?.period_code ?? null}
 totalHC={totalHC}
 currency={currency}
 refreshedAt={null}
 />

 {/* Population context + Driver bullets */}
 {!noHC && (
 <div className="flex gap-4">
 <div className="min-w-0 flex-[65]">
 <PopulationContextTable population={population?.population_breakdown} loading={popLoading} error={popError} />
 </div>
 <div className="flex-[35] min-w-0">
 <DriverBullets exposure={exposure} population={population} aboveQuotaPct={aboveQuotaPct} />
 </div>
 </div>
 )}
 </div>
 )}
 </div>

 {/* ═══════════════════════════════════════════════════════════════════════
 SECTION 3 — SCENARIO ACCURACY
 CFO question: "How well did my past scenarios predict actuals?"
 Only meaningful post-close. Clean empty state when period is open —
 no "Period Still Open" dead-end card, just a contextual note.
 ═══════════════════════════════════════════════════════════════════════ */}

 <ScenarioAccuracyPanel fiscalPeriodId={activePeriodId} />

 </div>
 );
}
